import axios from "axios";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { createLogger } from "debug/DebugingLogger";

const log = createLogger("Canvas utils");

/// D24 at the transfer door: the table's connection is scoped to another org,
/// or can't be resolved at all. The server's message names which.
const CONNECTION_MOVE_REFUSALS: ReadonlySet<string> = new Set([
  "connection_out_of_org",
  "connection_unresolvable",
]);

/// Surface an import failure as a user-visible toast.
///
/// Replaces the older `handleTableImported`, which only emitted a toast
/// for the "Table exists already" message and returned `false` for
/// everything else — silently swallowing FGA permission denials, network
/// failures, and validation errors. This helper ALWAYS emits a toast.
///
/// Pattern-matches in priority order:
/// 1. "Table exists already" → "already imported" (existing case)
/// 2. Axios with structured `error_code === "table_locked_to_parent"` →
///    parent-lock copy
/// 3. Axios status (403 / 404 / 5xx / network) → curated copy
/// 4. Fallback → sanitized raw message
///
/// `tableName` is interpolated into copy when provided; when empty, the
/// helper falls back to generic phrasing (used by the Google Sheet
/// import path, which doesn't carry a table name at the throw site).
export const notifyImportFailure = (
  error: unknown,
  tableName: string,
  existsAlreadyFallback: string = "",
): void => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const label = tableName ? `"${tableName}"` : "this table";

  if (errorMessage.includes("Table exists already")) {
    showNotificationGlobal(
      tableName
        ? `Table "${tableName}" is already imported`
        : existsAlreadyFallback || "Table is already imported",
    );
    return;
  }

  if (axios.isAxiosError(error)) {
    if (!error.response) {
      showNotificationGlobal(
        `Couldn't reach the server — check your connection and try again`,
        "error",
      );
      return;
    }
    const status = error.response.status;
    const body = error.response.data as
      { error_code?: string; message?: string } | undefined;
    if (status === 403 && body?.error_code === "table_locked_to_parent") {
      showNotificationGlobal(
        body?.message ??
          `${label} stays with the credentials that connected it and can't be moved here`,
        "error",
      );
      return;
    }
    if (
      status === 403 &&
      CONNECTION_MOVE_REFUSALS.has(body?.error_code ?? "")
    ) {
      showNotificationGlobal(
        body?.message ??
          `${label} syncs through a connection scoped to another organization and can't be moved here`,
        "error",
      );
      return;
    }
    if (status === 403) {
      showNotificationGlobal(
        `You don't have permission to import ${label}`,
        "error",
      );
      return;
    }
    if (status === 404) {
      showNotificationGlobal(`${label} no longer exists`, "error");
      return;
    }
    if (status >= 500) {
      showNotificationGlobal(
        `Server error while importing ${label} — try again`,
        "error",
      );
      return;
    }
  }

  showNotificationGlobal(
    tableName
      ? `Failed to import "${tableName}": ${errorMessage}`
      : `Import failed: ${errorMessage}`,
    "error",
  );
};

/// Surface a structured `RequestTableImportResult` failure (the
/// conditional-import POST path) as a user-visible toast. Mirrors
/// `notifyImportFailure` but consumes the discriminated union directly
/// so the call site doesn't have to synthesize an Error.
export const notifyImportFailureFromResult = (
  result: {
    kind: string;
    status?: number;
    error_code?: string;
    message?: string;
  },
  tableName: string,
): void => {
  const label = tableName ? `"${tableName}"` : "this table";

  if (result.kind === "locked_to_parent") {
    showNotificationGlobal(
      result.message ??
        `${label} stays with the credentials that connected it and can't be moved here`,
      "error",
    );
    return;
  }

  const status = result.status ?? 0;
  const errorCode = result.error_code;
  const message = result.message;

  if (status === 403 && errorCode === "table_locked_to_parent") {
    showNotificationGlobal(
      message ??
        `${label} stays with the credentials that connected it and can't be moved here`,
      "error",
    );
    return;
  }
  if (status === 403 && CONNECTION_MOVE_REFUSALS.has(errorCode ?? "")) {
    showNotificationGlobal(
      message ??
        `${label} syncs through a connection scoped to another organization and can't be moved here`,
      "error",
    );
    return;
  }
  if (status === 403) {
    showNotificationGlobal(
      `You don't have permission to import ${label}`,
      "error",
    );
    return;
  }
  if (status === 404) {
    showNotificationGlobal(`${label} no longer exists`, "error");
    return;
  }
  if (status === 0) {
    showNotificationGlobal(
      `Couldn't reach the server — check your connection and try again`,
      "error",
    );
    return;
  }
  if (status >= 500) {
    showNotificationGlobal(
      `Server error while requesting access to ${label} — try again`,
      "error",
    );
    return;
  }

  showNotificationGlobal(
    message
      ? `Could not request access to ${label}: ${message}`
      : `Could not request access to ${label}`,
    "error",
  );
};

export const cleanup = (cleanupFn: () => void, errorMessage: string): void => {
  try {
    cleanupFn();
  } catch (error) {
    log.warning(errorMessage, error);
  }
};
